import type { IntlShape } from 'react-intl'
import { defineMessages } from 'react-intl'
import { BENEFIT_GROUP_CONFIGS } from 'luxPlus/constants/benefits'
import { objectEntries, objectKeys } from 'lib/object/objectUtils'

const PARTNER_PROMOS_MESSAGES = defineMessages({
  title: {
    id: '3sfFn4QEgDB6PA3zXuZi',
    defaultMessage: 'Special promotions from our trusted partners',
    description: 'Title for grouped partner promotions benefit on landing page',
  },
  shortTitle: {
    id: 'EDKWZi8YcXm2uQxy64hO',
    defaultMessage: 'Exclusive partner offers',
    description: 'Short title for grouped partner promotions benefit on landing page',
  },
  description: {
    id: 'SR_9SJseqpREh-K78S0Z',
    defaultMessage: 'Special promotions from our trusted partners',
    description: 'Description for grouped partner promotions benefit on landing page',
  },
})

const DISCOUNTED_OFFER_LABELS = defineMessages({
  discounted_flash: {
    id: 'ePWFIztUa9nIjXtEBduh',
    defaultMessage: 'hotels',
    description: 'Label for discounted hotels when grouped on landing page',
  },
  discounted_experiences: {
    id: '0vroH6AdeJbhYLolVDwT',
    defaultMessage: 'experiences',
    description: 'Label for discounted experiences when grouped on landing page',
  },
  discounted_tours: {
    id: 'sEOvm2Ea4-G7DA85dMrJ',
    defaultMessage: 'tours',
    description: 'Label for discounted tours when grouped on landing page',
  },
})

function joinLabels(labels: Array<string>, locale: string): string {
  return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(labels)
}

function findGroup(
  type: App.LuxPlusBenefitType,
  allowedGroups: ReadonlyArray<App.LuxPlusBenefitGroupType>,
) {
  for (const [groupType, config] of objectEntries(BENEFIT_GROUP_CONFIGS)) {
    if (allowedGroups.includes(groupType) && config.types.includes(type)) {
      return { groupType, config }
    }
  }
}

function buildDiscountedOffersCopy(groupMembers: Array<App.LuxPlusBenefitItem>, intl: IntlShape): { title: string; shortTitle: string; description?: string } {
  const benefitTypesPresent = new Set(groupMembers.map(b => b.type))
  const offerLabels = objectEntries(DISCOUNTED_OFFER_LABELS)
    .filter(([type]) => benefitTypesPresent.has(type))
    .map(([, msg]) => intl.formatMessage(msg))
  const categories = joinLabels(offerLabels, intl.locale)

  return {
    title: intl.formatMessage(
      {
        id: 'Wtcp_qIdWr6dQKSIxZdp',
        defaultMessage: 'Discounted pricing on our best {categories}',
        description: 'Title for grouped discounted travel benefit on landing page',
      },
      { categories },
    ),
    shortTitle: intl.formatMessage(
      {
        id: 'ZGyQqqt2QHwo7QdQEHsh',
        defaultMessage: 'Save on {categories}',
        description: 'Short title for grouped discounted travel benefit on landing page',
      },
      { categories },
    ),
  }
}

/**
 * Build display copy (title / shortTitle / description) for a grouped benefit.
 *
 * Two strategies are used depending on the group:
 *  - **Static** (e.g. `partner_promos`): fixed i18n messages, members ignored.
 *  - **Dynamic** (e.g. `discounted_offers`): copy derived from which member
 *    types are present (e.g. "Save on hotels, experiences & tours").
 *
 * When adding a new group type, add a case here and a matching builder.
 */
function buildGroupCopy(
  groupType: App.LuxPlusBenefitGroupType,
  groupMembers: Array<App.LuxPlusBenefitItem>,
  intl: IntlShape,
): { title: string; shortTitle: string; description?: string } {
  switch (groupType) {
    case 'partner_promos': {
      return {
        title: intl.formatMessage(PARTNER_PROMOS_MESSAGES.title),
        shortTitle: intl.formatMessage(PARTNER_PROMOS_MESSAGES.shortTitle),
        description: intl.formatMessage(PARTNER_PROMOS_MESSAGES.description),
      }
    }
    case 'discounted_offers':
      return buildDiscountedOffersCopy(groupMembers, intl)
  }
}

const ALL_GROUP_TYPES = objectKeys(BENEFIT_GROUP_CONFIGS)

/**
 * Collapses benefits into grouped entries for the specified group types.
 * Benefits whose type does not belong to an allowed group pass through unchanged.
 * Benefits only gets consumed once.
 */
function groupLuxPlusBenefits(
  benefits: Array<App.LuxPlusBenefitItem>,
  intl: IntlShape,
  groups: ReadonlyArray<App.LuxPlusBenefitGroupType> = ALL_GROUP_TYPES,
): Array<App.LuxPlusBenefitView> {
  const consumed = new Set<App.LuxPlusBenefitType>()
  const result: Array<App.LuxPlusBenefitView> = []

  for (const benefit of benefits) {
    if (consumed.has(benefit.type)) continue

    const group = findGroup(benefit.type, groups)
    if (!group) {
      result.push(benefit)
      continue
    }

    const { groupType, config } = group
    const members = benefits.filter(b => config.types.includes(b.type))
    for (const t of config.types) consumed.add(t)
    result.push({ type: groupType, members, ...buildGroupCopy(groupType, members, intl) })
  }

  return result
}

export default groupLuxPlusBenefits
